import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
    Box, Card, CardContent, CardHeader,
    Typography, Divider, TextField,
    IconButton, Grid, Chip,
    Button, MenuItem
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import {
    collection, getDocs, addDoc,
    updateDoc, deleteDoc, doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

function Rules() {
    const auth = getAuth();

    const [rules, setRules] = useState([]);
    const [form, setForm] = useState({
        title: "",
        content: "",
        status: "active"
    });
    const [editId, setEditId] = useState(null);

    const [userData, setUserData] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUserData(user);

                const snap = await getDocs(collection(db, "authroles"));
                snap.forEach(d => {
                    if (d.id === user.uid) {
                        setPropertyId(d.data().propertyId);
                    }
                });
            }
        });
        return () => unsub();
    }, []);

    /* ================= FETCH ================= */
    const fetchRules = async () => {
        if (!userData || !propertyId) return;

        const snap = await getDocs(collection(db, "property_rules"));
        const list = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(
                r => r.userId === userData.uid && r.propertyId === propertyId
            );

        setRules(list);
    };

    useEffect(() => {
        fetchRules();
    }, [userData, propertyId]);

    /* ================= ADD / UPDATE ================= */
    const saveRule = async () => {
        if (!form.title || !form.content) return;

        if (editId) {
            await updateDoc(doc(db, "property_rules", editId), form);
            Swal.fire("Updated", "Rule updated successfully", "success");
        } else {
            await addDoc(collection(db, "property_rules"), {
                ...form,
                userId: userData.uid,
                propertyId,
                createdAt: new Date()
            });
            Swal.fire("Added", "Rule added successfully", "success");
        }

        setForm({ title: "", content: "", status: "active" });
        setEditId(null);
        fetchRules();
    };

    /* ================= DELETE ================= */
    const deleteRule = async (id) => {
        await deleteDoc(doc(db, "property_rules", id));
        Swal.fire("Deleted", "Rule removed", "success");
        fetchRules();
    };

    /* ================= EDIT ================= */
    const editRule = (rule) => {
        setForm({
            title: rule.title,
            content: rule.content,
            status: rule.status
        });
        setEditId(rule.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
            <Menu />

            <Box flex={1} p={3} className="content-wrapper">
                <Typography variant="h5" fontWeight={700} mb={3}>
                    📝 Rules & Regulations
                </Typography>

                {/* ================= FORM ================= */}
                <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
                    <CardHeader title={editId ? "Edit Rule" : "Add New Rule"} />
                    <Divider />
                    <CardContent>
                        <Grid className="row" spacing={2}>
                            <Grid item xs={12} className="col-sm-3" md={6}>
                                <TextField
                                    label="Rule Title"
                                    fullWidth
                                    size="small"
                                    value={form.title}
                                    onChange={(e) =>
                                        setForm({ ...form, title: e.target.value })
                                    }
                                />
                            </Grid>


                            <Grid item xs={12} className="col-sm-4">
                                <TextField
                                    label="Rule Description"
                                    fullWidth
                                    multiline
                                    value={form.content}
                                    onChange={(e) =>
                                        setForm({ ...form, content: e.target.value })
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} className="col-sm-2" md={6}>
                                <TextField
                                    select
                                    label="Status"
                                    size="small"
                                    fullWidth
                                    value={form.status}
                                    onChange={(e) =>
                                        setForm({ ...form, status: e.target.value })
                                    }
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} className="col-sm-3">
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={saveRule}
                                >
                                    {editId ? "Update Rule" : "Add Rule"}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* ================= RULE LIST (TABLE VIEW) ================= */}
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                    <CardHeader title="Rules List" />
                    <Divider />

                    <CardContent>
                        {rules.length === 0 ? (
                            <Typography color="text.secondary">
                                No rules added yet.
                            </Typography>
                        ) : (
                            <Box sx={{ overflowX: "auto" }}>
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "collapse"
                                    }}
                                >
                                    <thead>
                                        <tr style={{ background: "#f5f7fa" }}>
                                            <th style={thStyle}>Title</th>
                                            <th style={thStyle}>Content</th>
                                            <th style={thStyle}>Status</th>
                                            <th style={thStyle}>Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {rules.map((rule) => (
                                            <tr key={rule.id} style={{ borderBottom: "1px solid #eee" }}>
                                                <td style={tdStyle}>
                                                    <Typography fontWeight={600}>
                                                        {rule.title}
                                                    </Typography>
                                                </td>

                                                <td style={tdStyle}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            maxWidth: 400,
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word"
                                                        }}
                                                    >
                                                        {rule.content}
                                                    </Typography>
                                                </td>

                                                <td style={tdStyle}>
                                                    <Chip
                                                        label={rule.status.toUpperCase()}
                                                        color={rule.status === "active" ? "success" : "default"}
                                                        size="small"
                                                    />
                                                </td>

                                                <td style={tdStyle}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => editRule(rule)}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>

                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => deleteRule(rule.id)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        )}
                    </CardContent>
                </Card>

            </Box>
        </Box>
    );
}

const thStyle = {
    textAlign: "left",
    padding: "12px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#555"
};

const tdStyle = {
    padding: "12px",
    verticalAlign: "top"
};


export default Rules;
